import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Eye, Heart, FileDown } from 'lucide-react';
import { Property } from '../../types';
import { apiService } from '../../services/api';
import { BrochureDownloadModal } from '../BrochureDownloadModal';

interface PropertyCardProps {
  property: Property;
  showFeaturedBadge?: boolean;
}

export const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  showFeaturedBadge = true
}) => {
  const [showBrochureModal, setShowBrochureModal] = useState(false);

  // Handle both old structure (images array) and new structure (flat primary_image_path)
  const primaryImage = property.images?.find(img => img.is_primary) 
    || property.images?.[0] 
    || (property.primary_image_path ? { file_path: property.primary_image_path, is_primary: true } : null);
    
  const imageUrl = primaryImage 
    ? apiService.getImageUrl(primaryImage.file_path)
    : '/api/placeholder/400/300';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'sold':
        return 'bg-red-100 text-red-800';
      case 'rented':
        return 'bg-blue-100 text-blue-800';
      case 'under_construction':
        return 'bg-yellow-100 text-yellow-800';
      case 'off_market':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'sold':
        return 'Sold';
      case 'rented':
        return 'Rented';
      case 'under_construction':
        return 'Under Construction';
      case 'off_market':
        return 'Off Market';
      default:
        return status;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
      {/* Image Container */}
      <div className="relative overflow-hidden">
        <Link to={`/properties/${property.slug}`} className="block">
          <div className="relative aspect-[4/3]">
            <img
              src={imageUrl}
              alt={property.title}
              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500 ease-in-out"
            />
            <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
          </div>
        </Link>
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {showFeaturedBadge && property.is_featured && (
            <span className="bg-secondary text-primary px-3 py-1 rounded-full text-xs font-semibold">
              Featured
            </span>
          )}
          {property.is_exclusive && (
            <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
              Exclusive
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(property.status)}`}>
            {getStatusLabel(property.status)}
          </span>
        </div>

        {/* Actions */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <button className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors duration-200">
            <Heart className="w-4 h-4 text-gray-600" />
          </button>
        </div>

      </div>

      {/* Content */}
      <div className="p-6">
        {/* Category */}
        {property.category && (
          <div className="flex items-center mb-2">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: property.category.color }}
            ></div>
            <span className="text-sm text-gray-600">{property.category.name}</span>
          </div>
        )}

        {/* Title */}
        <Link to={`/properties/${property.slug}`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-primary transition-colors duration-200 line-clamp-2">
            {property.title}
          </h3>
        </Link>

        {/* Location */}
        <div className="flex items-center text-gray-600 mb-4">
          <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
          <span className="text-sm truncate">
            {property.location}
          </span>
        </div>

        {/* Description */}
        {property.description && (
          <p className="text-gray-600 text-sm line-clamp-2 mb-4">
            {property.description}
          </p>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
          {property.primary_brochure_id && (
            <button
              type="button"
              onClick={() => setShowBrochureModal(true)}
              className="flex items-center gap-1 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors duration-200"
            >
              <FileDown className="w-4 h-4" />
              Brochure
            </button>
          )}
          <Link
            to={`/properties/${property.slug}`}
            className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors duration-200"
          >
            View Details
          </Link>
        </div>
      </div>

      {property.primary_brochure_id && (
        <BrochureDownloadModal
          isOpen={showBrochureModal}
          propertyId={property.id}
          propertyTitle={property.title}
          brochureId={property.primary_brochure_id}
          onClose={() => setShowBrochureModal(false)}
        />
      )}
    </div>
  );
};